import { Button } from '@seen/ui'
import { type JSX, useEffect, useRef, useState } from 'react'

import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import { DocumentPickerView } from '#src/components/document-picker/DocumentPickerView.tsx'

interface DocumentPickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  documents: DocumentPickerDocument[]
  onInsert: (selectedIds: string[]) => void
  // Ids to pre-check when the dialog opens (e.g. documents already chosen by the
  // caller), so reopening the picker reflects the current selection. Defaults to
  // none.
  initialSelectedIds?: string[]
}

export function DocumentPickerDialog({
  open,
  onOpenChange,
  documents,
  onInsert,
  initialSelectedIds,
}: DocumentPickerDialogProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null)

  const firstDocumentId = documents[0]?.id ?? null
  // Snapshot ids into a stable key so the seed effect re-runs on open (and if the
  // caller's selection changes) without depending on a fresh array reference.
  const initialSelectedKey = (initialSelectedIds ?? []).join(',')
  useEffect(() => {
    if (open) {
      const seededIds = initialSelectedKey ? initialSelectedKey.split(',') : []
      setSelectedIds(seededIds)
      setActiveDocumentId(seededIds[0] ?? firstDocumentId)
    }
  }, [open, firstDocumentId, initialSelectedKey])

  const handleInsertRef = useRef<() => void>(() => {})
  handleInsertRef.current = () => {
    if (selectedIds.length > 0) {
      onInsert(selectedIds)
    } else if (activeDocumentId) {
      onInsert([activeDocumentId])
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }
    /* Skip the Enter keypress that opened the dialog */
    let ready = false
    const frameId = requestAnimationFrame(() => {
      ready = true
    })
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && ready) {
        e.preventDefault()
        handleInsertRef.current()
      }
    }
    window.addEventListener('keydown', handler)
    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener('keydown', handler)
    }
  }, [open])

  const hasSelection = selectedIds.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay
        className="mt-4 h-[calc(100dvh-32px)] max-h-[calc(100dvh-32px)] max-w-[calc(100vw-32px)]"
        notScrollable
        hideCloseButton
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DocumentPickerView
          documents={documents}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
          activeDocumentId={activeDocumentId}
          onActiveDocumentChange={setActiveDocumentId}
          onItemDoubleClick={(id) => {
            const ids = new Set(selectedIds)
            ids.add(id)
            onInsert([...ids])
          }}
          onClose={() => onOpenChange(false)}
          listFooter={
            <Button
              className="absolute right-4 bottom-4 shadow-lg"
              onClick={() => handleInsertRef.current()}
            >
              {hasSelection ? `Insert (${selectedIds.length})` : 'Insert'}
            </Button>
          }
        />
      </DialogDisplay>
    </Dialog>
  )
}
